import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusBadge, TypeBadge } from "@/components/StatusBadge";
import { Building2, Briefcase, Mail, MapPin, Megaphone, TrendingUp, FolderOpen } from "lucide-react";

const FP_COLORS = ["#0f766e", "#2563eb", "#7c3aed", "#ea580c", "#059669", "#db2777", "#dc2626", "#0891b2", "#a16207", "#4f46e5", "#0d9488", "#ca8a04", "#9333ea"];

export default function AdminReports({ opps, notices }) {
  const [stats, setStats] = useState({});
  const [sc, setSc] = useState({});
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/scorecard").then((r) => setSc(r.data)).catch(() => {});
  }, []);

  const trend = stats.registration_trend || [];
  const maxT = Math.max(1, ...trend.map((t) => t.count));
  const footprint = sc.footprint || [];
  const maxF = Math.max(1, ...footprint.map((f) => f.count));
  const sectorFp = sc.sector_footprint || [];
  const maxS = Math.max(1, ...sectorFp.map((f) => f.count));

  const scards = [
    { icon: Briefcase, label: "Opportunities this month", value: sc.posted_opportunities?.month ?? 0, sub: `${sc.posted_opportunities?.total ?? 0} total` },
    { icon: Building2, label: "SMMEs registered this month", value: sc.smmes_registered?.month ?? 0, sub: `${sc.total_smmes?.total ?? 0} in database` },
    { icon: Briefcase, label: "Total buyers", value: sc.total_buyers?.total ?? 0, sub: `${sc.total_buyers?.month ?? 0} this month` },
    { icon: Mail, label: "Email notifications sent", value: sc.notifications?.total ?? 0, sub: `${sc.notifications?.month ?? 0} this month` },
  ];

  return (
    <div className="space-y-6" data-testid="admin-reports">
      {/* Monthly registered SMMEs */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-report-trend">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><TrendingUp className="h-4 w-4" /> Monthly registered SMMEs</div>
        <h3 className="mt-1 font-display text-xl font-bold text-slate-900">New SMMEs per month · {stats.smmes_month ?? 0} this month</h3>
        <div className="mt-6 flex items-end gap-6 overflow-x-auto pb-2">
          {trend.map((t) => (
            <div key={t.label} className="flex flex-col items-center" style={{ minWidth: 48 }}>
              <div className="flex h-40 flex-col items-center justify-end">
                <span className="mb-1 text-xs font-bold text-slate-700">{t.count}</span>
                <div className="w-10 rounded-t bg-blue-600" style={{ height: `${Math.max(4, Math.round((t.count / maxT) * 140))}px` }} />
              </div>
              <span className="mt-2 text-xs text-slate-500">{t.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Procurement scorecard */}
      <div>
        <h3 className="font-display text-lg font-bold text-slate-900">Procurement scorecard · {sc.month_label || "this month"}</h3>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {scards.map((c) => (
            <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`admin-scard-${c.label}`}>
              <div className="flex items-start justify-between">
                <span className="text-xs font-bold uppercase leading-tight tracking-wide text-slate-500">{c.label}</span>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100"><c.icon className="h-4 w-4 text-slate-600" /></span>
              </div>
              <div className="mt-3 font-display text-4xl font-extrabold text-slate-900">{c.value}</div>
              <div className="mt-1 text-xs text-slate-500">{c.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Community footprint */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-community-footprint">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><MapPin className="h-4 w-4" /> Community footprint</div>
        <h3 className="mt-1 font-display text-xl font-bold text-slate-900">SMMEs registered per village / town</h3>
        {footprint.length === 0 ? (
          <p className="mt-6 rounded-lg border border-dashed border-slate-300 py-10 text-center text-slate-500">No location data captured yet.</p>
        ) : (
          <div className="mt-6 flex items-end gap-4 overflow-x-auto pb-2">
            {footprint.slice(0, 15).map((f) => (
              <div key={f.town} className="flex flex-col items-center" style={{ minWidth: 52 }}>
                <div className="flex h-40 flex-col items-center justify-end">
                  <span className="mb-1 text-xs font-bold text-slate-700">{f.count}</span>
                  <div className="w-9 rounded-t bg-teal-700" style={{ height: `${Math.max(4, Math.round((f.count / maxF) * 140))}px` }} />
                </div>
                <span className="mt-2 w-16 truncate text-center text-[10px] text-slate-500" title={f.town}>{f.town}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sector footprint */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-sector-footprint">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Briefcase className="h-4 w-4" /> Sector footprint</div>
        <h3 className="mt-1 font-display text-xl font-bold text-slate-900">SMMEs registered per sector</h3>
        {sectorFp.length === 0 ? (
          <p className="mt-6 rounded-lg border border-dashed border-slate-300 py-10 text-center text-slate-500">No sector data captured yet.</p>
        ) : (
          <div className="mt-6 flex items-end gap-4 overflow-x-auto pb-2">
            {sectorFp.slice(0, 13).map((f, i) => (
              <div key={f.sector} className="flex flex-col items-center" style={{ minWidth: 56 }}>
                <div className="flex h-40 flex-col items-center justify-end">
                  <span className="mb-1 text-xs font-bold text-slate-700">{f.count}</span>
                  <div className="w-9 rounded-t" style={{ height: `${Math.max(4, Math.round((f.count / maxS) * 140))}px`, background: FP_COLORS[i % FP_COLORS.length] }} />
                </div>
                <span className="mt-2 w-16 truncate text-center text-[10px] text-slate-500" title={f.sector}>{f.sector}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All opportunities */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-report-opps">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><FolderOpen className="h-4 w-4" /> All opportunities posted by buyers</div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Buyer</th><th className="px-4 py-3">Responses</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(opps || []).length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No opportunities posted yet.</td></tr>}
              {(opps || []).map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`admin-report-opp-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{o.reference}</td>
                  <td className="px-4 py-3"><TypeBadge type={o.type} /></td>
                  <td className="px-4 py-3 font-medium text-slate-800">{o.title}</td>
                  <td className="px-4 py-3 text-slate-600">{o.buyer_company}</td>
                  <td className="px-4 py-3 font-mono">{o.response_count}</td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* All notices */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-report-notices">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Megaphone className="h-4 w-4" /> All notices posted</div>
        <div className="mt-4 space-y-3">
          {(notices || []).length === 0 && <p className="rounded-lg border border-dashed border-slate-300 py-8 text-center text-slate-500">No notices posted yet.</p>}
          {(notices || []).map((n) => (
            <div key={n.id} className="rounded-lg border border-slate-200 p-4" data-testid={`admin-report-notice-${n.id}`}>
              <div className="flex items-center gap-2">
                <h4 className="font-display font-bold text-slate-900">{n.title}</h4>
                <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{n.category || "Announcements"}</span>
              </div>
              <p className="mt-1 whitespace-pre-line text-sm text-slate-600">{n.body}</p>
              <p className="mt-2 text-xs font-medium text-slate-400">{n.author} · {new Date(n.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
